
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface JsonLdProps {
  data: object;
}

export function JsonLd({ data }: JsonLdProps) {
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(data);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, [data]);

  return null;
}

export function usePageSEO(title: string, description: string, keywords?: string) {
  const location = useLocation();

  useEffect(() => {
    // Update title
    document.title = title;
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', description);
    }
    
    // Update meta keywords
    if (keywords) {
      const metaKeywords = document.querySelector('meta[name="keywords"]');
      if (metaKeywords) {
        metaKeywords.setAttribute('content', keywords);
      }
    }
    
    // Update canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL}${location.pathname}`);
    
    // Update last-modified
    let lastModified = document.querySelector('meta[name="last-modified"]');
    if (!lastModified) {
      lastModified = document.createElement('meta');
      lastModified.setAttribute('name', 'last-modified');
      document.head.appendChild(lastModified);
    }
    lastModified.setAttribute('content', new Date().toISOString().split('T')[0]);
    
    // Update OG tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', title);
    }
    
    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', description);
    }
    
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute('content', `${import.meta.env.VITE_SITE_URL}${location.pathname}`);
    }
    
    // Update Twitter Card
    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitle) {
      twitterTitle.setAttribute('content', title);
    }
    
    const twitterDescription = document.querySelector('meta[name="twitter:description"]');
    if (twitterDescription) {
      twitterDescription.setAttribute('content', description);
    }
  }, [title, description, keywords, location.pathname]);
}

// Schema.org JSON-LD data generators
export const generateOrganizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "株式会社SUNFIELD",
  "url": import.meta.env.VITE_SITE_URL,
  "logo": "https://static.readdy.ai/image/78304d8d02dcd7906feadaea6da56541/f6ca7598e3b085ce1eaa1cd058912520.png",
  "description": "京都を拠点とする運送会社SUNFIELDです。一般貨物運送、軽貨物配送、定期便、チャーター便など、関西エリアで安全・迅速・確実な物流サービスを提供しています。",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "八幡戸津東代64-1",
    "addressLocality": "八幡市",
    "addressRegion": "京都府",
    "addressCountry": "JP"
  },
  "telephone": "090-9801-4659",
  "sameAs": []
});

export const generateWebSiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "株式会社SUNFIELD",
  "url": import.meta.env.VITE_SITE_URL,
  "description": "京都を拠点とする運送会社SUNFIELDです。一般貨物運送、軽貨物配送、定期便、チャーター便など、関西エリアで安全・迅速・確実な物流サービスを提供しています。",
  "publisher": {
    "@type": "Organization",
    "name": "株式会社SUNFIELD"
  }
});

export const generateServiceSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Service",
  "name": "運送・物流サービス",
  "description": "一般貨物運送、軽貨物配送、定期便、チャーター便など、関西エリアで安全・迅速・確実な物流サービスを提供しています。",
  "provider": {
    "@type": "Organization",
    "name": "株式会社SUNFIELD",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "八幡戸津東代64-1",
      "addressLocality": "八幡市",
      "addressRegion": "京都府",
      "addressCountry": "JP"
    },
    "telephone": "090-9801-4659"
  },
  "areaServed": {
    "@type": "Place",
    "name": "関西エリア"
  },
  "serviceType": ["一般貨物運送", "軽貨物配送", "定期便", "チャーター便"]
});

export const generateJobPostingSchema = (jobTitle: string, description: string) => ({
  "@context": "https://schema.org",
  "@type": "JobPosting",
  "title": jobTitle,
  "description": description,
  "hiringOrganization": {
    "@type": "Organization",
    "name": "株式会社SUNFIELD",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "八幡戸津東代64-1",
      "addressLocality": "八幡市",
      "addressRegion": "京都府",
      "addressCountry": "JP"
    }
  },
  "jobLocation": {
    "@type": "Place",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "八幡戸津東代64-1",
      "addressLocality": "八幡市",
      "addressRegion": "京都府",
      "addressCountry": "JP"
    }
  },
  "employmentType": "FULL_TIME",
  "datePosted": new Date().toISOString().split('T')[0]
});

export const generateFAQSchema = (faqs: Array<{question: string, answer: string}>) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map(faq => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.answer
    }
  }))
});
